import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, PackageCheck, PackageX, ScanLine } from "lucide-react";
import type { ProductMatch } from "@/lib/shop-search";
import type { Product } from "@/lib/catalog";
import { Button } from "@/components/ui/button";

export interface SearchSuggestProps {
  /** Unique relationship to the search field that owns these results. */
  id: string;
  /** Top matches (already sorted by score). */
  matches: ProductMatch[];
  /** Total number of matches for the current query. */
  totalCount: number;
  /** Whether the query currently looks like an OEM/SKU code. */
  oemLike: boolean;
  /** Index of the keyboard-highlighted item. */
  activeIndex: number;
  onActiveIndex: (i: number) => void;
  productImageFor: (p: Product) => string | null;
  /** Called when the dropdown should close (pick, escape, show more). */
  onClose: () => void;
  /** Called when the user wants the full result list (submits + scrolls). */
  onShowMore: () => void;
}

function formatPrice(p: Product) {
  return new Intl.NumberFormat("en-IE", { style: "currency", currency: p.currency ?? "EUR" }).format(p.price);
}

/**
 * Live search dropdown shown under the hero search while typing.
 * Renders the top 2 product matches plus a "Show all" footer.
 */
export function SearchSuggest({
  id,
  matches,
  totalCount,
  oemLike,
  activeIndex,
  onActiveIndex,
  productImageFor,
  onClose,
  onShowMore,
}: SearchSuggestProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const items = matches.slice(0, 2);

  useEffect(() => {
    const el = listRef.current?.querySelectorAll<HTMLElement>("[data-suggest-item]")[activeIndex];
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!items.length) return null;

  return (
    <div
      ref={listRef}
      id={id}
      role="region"
      aria-label="Search suggestions"
      className="absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-ink/10 bg-paper text-ink shadow-elevated"
    >
      <p className="flex items-center gap-1.5 border-b border-ink/5 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-ink/50">
        {oemLike ? <ScanLine className="h-3 w-3" /> : null}
        {oemLike ? "OEM / SKU match" : "Top matches"}
      </p>
      {items.map((m, i) => {
        const p = m.product;
        const img = productImageFor(p);
        const content = (
          <>
            <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-ink/5">
              {img ? (
                <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <PackageCheck className="h-5 w-5 text-ink/30" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold leading-tight">{p.name}</span>
              <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink/60">
                <span className="font-mono">{p.sku}</span>
                {m.matchedOem && m.matchedOem !== p.sku && (
                  <span className="rounded-md bg-sky-pop/20 px-1.5 py-px font-mono font-bold text-ink">OEM {m.matchedOem}</span>
                )}
                {p.inStock ? (
                  <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                    <PackageCheck className="h-3 w-3" /> In stock
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-ink/50">
                    <PackageX className="h-3 w-3" /> Out of stock
                  </span>
                )}
              </span>
            </span>
            <span className="shrink-0 text-sm font-black">{formatPrice(p)}</span>
          </>
        );
        const cls = `flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
          i === activeIndex ? "bg-sky-pop/15" : "hover:bg-ink/5"
        }`;
        return p.slug ? (
          <Link
            key={p.id}
            to="/p/$slug"
            params={{ slug: p.slug }}
            data-suggest-item
            onClick={onClose}
            onMouseEnter={() => onActiveIndex(i)}
            className={cls}
          >
            {content}
          </Link>
        ) : (
          <div key={p.id} data-suggest-item onMouseEnter={() => onActiveIndex(i)} className={cls}>
            {content}
          </div>
        );
      })}
      <Button variant="ghost"
        type="button"
        onClick={onShowMore}
        className="press flex w-full items-center justify-center gap-1.5 border-t border-ink/5 bg-ink/[0.03] px-4 py-2.5 text-xs font-black uppercase tracking-wider text-ink hover:bg-ink/[0.06]"
      >
        Show all {totalCount} result{totalCount === 1 ? "" : "s"} <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
